// Rack de habitaciones — Hotel → Operación → Rack.
// Cuadrícula de días (7/14/30): filas = habitaciones agrupadas por PISO (o por
// tipo), columnas = días desde la fecha elegida. Cada reserva con habitación es
// una barra horizontal sobre sus noches.
//
// Navegación (fase 1 del rediseño UX): la grilla tiene scroll propio con el
// encabezado de días y la fila «Libres» fijos arriba; los grupos se pliegan y,
// plegados, muestran ocupadas/total por día como mapa de calor; los chips de piso
// saltan al grupo; el buscador (tecla «/») salta a la habitación, huésped o
// código y lo resalta; la banda «Por asignar» reúne arriba las reservas sin
// habitación y los bloqueos por tipo. Las preferencias (agrupar, densidad, días,
// grupos plegados) se recuerdan por empresa y usuario.
//
// Trabajo desde el rack (fase 2): clic en una barra abre una vista previa con
// acciones rápidas (depósito, asignar, check-in, check-out, cambiar habitación);
// el detalle y la nueva reserva se abren como página (el rack queda montado y
// conserva su estado); la vista previa y la confirmación son paneles integrados;
// clic (o arrastre) sobre celdas libres inicia una reserva con la habitación y
// las fechas ya puestas. El estado de la barra lleva icono, no solo color.
//
// Arrastrar y soltar (fase 3, ratón): mover la barra a otra fila cambia la
// habitación (o la quita, soltándola en «Por asignar»); moverla en horizontal
// corre las fechas; las asas de los bordes estiran la llegada o la salida. Al
// soltar SIEMPRE se confirma: hotel.rack_simular muestra el antes/después exacto
// (diferencia de precio, avisos, error) y hotel.rack_aplicar lo ejecuta. La
// vista previa ofrece «Sugerir habitación» (hotel.sugerir_habitacion) y sus
// acciones son la alternativa sin arrastre (teclado y tablet).
import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BedDouble, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, CircleDashed, HelpCircle, Home, Inbox,
  Loader2, LogOut, Plus, Search, Sparkles, UserX, Wrench, X, Zap,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import {
  DisponibilidadTipo, ESTADO_RESERVA, LIMPIEZA, MANT, Rack, RackReserva,
  QK, fmtFecha, hoyISO, hotelRpc, money, sumarDias,
} from './shared';
import { ReservaDetalle, type Accion } from './ReservaDetalle';
import { NuevaReserva, type NuevaReservaInicial } from './NuevaReserva';
import { useAvisosHabitacionLista } from './avisosHk';
import { LOCALE } from '@/lib/utils/regional';

type Habitacion = Rack['habitaciones'][number];
type Bloqueo = Rack['bloqueos'][number];
type Agrupar = 'piso' | 'tipo';
type Densidad = 'compacta' | 'comoda';
type Drawer = null | { tipo: 'reserva'; id: string; accion?: Accion } | { tipo: 'nueva'; inicial: NuevaReservaInicial };
type ModoArrastre = 'mover' | 'inicio' | 'fin';
// hab: destino del arrastre — undefined = sin fila bajo el ratón, null = «Por asignar».
type Arrastre = { r: RackReserva; modo: ModoArrastre; x0: number; y0: number; colW: number; activo: boolean; dd: number; hab: string | null | undefined };
type Movimiento = { r: RackReserva; d: { habitacion_id?: string | null; llegada?: string; salida?: string } };
const MOVIBLES = new Set(['tentativa', 'confirmada', 'en_casa']);
const ESTADOS_ACTIVOS = new Set(['tentativa', 'confirmada', 'en_casa']);
type Seleccion = { habId: string; tipoId: string; i0: number; i1: number; max: number };
type Prefs = { agrupar: Agrupar; densidad: Densidad; dias: 7 | 14 | 30; plegados: string[]; libresDetalle: boolean };

const PREFS_DEF: Prefs = { agrupar: 'piso', densidad: 'compacta', dias: 14, plegados: [], libresDetalle: false };
const COL_INFO = 150;
const COL_DIA: Record<Prefs['dias'], number> = { 7: 120, 14: 76, 30: 40 };
const ALTO_FILA: Record<Densidad, number> = { compacta: 28, comoda: 40 };
const ESTADOS_OCUPAN = new Set(['tentativa', 'confirmada', 'en_casa', 'salida']);
const PUNTO_LIMPIEZA: Record<string, string> = {
  sucia: 'bg-red-500', limpia: 'bg-sky-500', inspeccionada: 'bg-emerald-500',
};

const natural = (a: string, b: string) => a.localeCompare(b, 'es', { numeric: true });

function diffDias(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);
}
const diaSemana = (iso: string) => new Date(`${iso}T12:00:00`).getDay(); // 0 dom … 6 sáb
const esFinde = (iso: string) => { const d = diaSemana(iso); return d === 0 || d === 6; };
const cubre = (desde: string, hasta: string, dia: string) => desde <= dia && dia < hasta;

// Fondo de calor para un grupo plegado según la fracción ocupada del día.
function calor(r: number): string {
  if (r <= 0) return '';
  if (r < 0.5) return 'bg-emerald-50 dark:bg-emerald-950/30';
  if (r < 0.8) return 'bg-amber-100 dark:bg-amber-900/30';
  if (r < 1) return 'bg-orange-200 dark:bg-orange-900/40';
  return 'bg-red-200 font-semibold dark:bg-red-900/50';
}

function usePrefs(clave: string): [Prefs, (p: Partial<Prefs>) => void] {
  const leer = (): Prefs => {
    try { const raw = localStorage.getItem(clave); if (raw) return { ...PREFS_DEF, ...JSON.parse(raw) }; } catch { /* sin almacenamiento */ }
    return PREFS_DEF;
  };
  const [prefs, setPrefs] = useState<Prefs>(leer);
  // La clave cambia cuando llega el usuario (empresa/usuario): releer.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPrefs(leer()); }, [clave]);
  const actualizar = (p: Partial<Prefs>) => setPrefs((prev) => {
    const next = { ...prev, ...p };
    try { localStorage.setItem(clave, JSON.stringify(next)); } catch { /* sin almacenamiento */ }
    return next;
  });
  return [prefs, actualizar];
}

interface Grupo {
  key: string; titulo: string; corto: string;
  habitaciones: Habitacion[];
}

export function RackHotel() {
  const { user } = useAuth();
  const [prefs, setPrefs] = usePrefs(`hotel_rack_prefs_${user?.empresa?.id ?? 'na'}_${user?.id ?? 'na'}`);
  const [desde, setDesde] = useState(hoyISO());
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [preview, setPreview] = useState<{ r: RackReserva; rect: DOMRect } | null>(null);
  const [hoverFila, setHoverFila] = useState<string | null>(null);
  const [sel, setSel] = useState<Seleccion | null>(null);
  const [drag, setDrag] = useState<Arrastre | null>(null);
  const [mov, setMov] = useState<Movimiento | null>(null);
  const dragRef = useRef<Arrastre | null>(null);
  const arrastradoRef = useRef(false);
  const [busca, setBusca] = useState('');
  const [resaltado, setResaltado] = useState<{ fila: string | null; reservas: Set<string> } | null>(null);
  const [colHover, setColHover] = useState<number | null>(null);
  const [verPorAsignar, setVerPorAsignar] = useState(true);

  const scrollRef = useRef<HTMLDivElement>(null);
  const cabeceraRef = useRef<HTMLDivElement>(null);
  const buscaRef = useRef<HTMLInputElement>(null);
  const filaRefs = useRef(new Map<string, HTMLDivElement>());

  const nDias = prefs.dias;
  const hasta = sumarDias(desde, nDias - 1);
  const colDia = COL_DIA[nDias];
  const alto = ALTO_FILA[prefs.densidad];

  const { data: rack, isFetching } = useQuery<Rack>({
    queryKey: [QK, 'rack', desde, hasta],
    queryFn: () => hotelRpc<Rack>('rack', { _desde: desde, _hasta: hasta }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: disp = [] } = useQuery<DisponibilidadTipo[]>({
    queryKey: [QK, 'disponibilidad', desde, hasta],
    queryFn: () => hotelRpc<DisponibilidadTipo[]>('disponibilidad', { _desde: desde, _hasta: hasta }),
    staleTime: 0, refetchOnMount: 'always',
  });

  const dias = useMemo(() => Array.from({ length: nDias }, (_, i) => sumarDias(desde, i)), [desde, nDias]);
  const hoy = rack?.fecha_hotel ?? hoyISO();

  const codigoTipo = useMemo(() => new Map(disp.map((t) => [t.tipo_id, t.codigo])), [disp]);
  const nombreTipo = useMemo(() => {
    const m = new Map(disp.map((t) => [t.tipo_id, t.nombre]));
    (rack?.habitaciones ?? []).forEach((h) => { if (!m.has(h.tipo_id)) m.set(h.tipo_id, h.tipo); });
    return m;
  }, [disp, rack]);

  const reservasPorHabitacion = useMemo(() => {
    const m = new Map<string, RackReserva[]>();
    (rack?.reservas ?? []).forEach((r) => { if (!r.habitacion_id) return; const arr = m.get(r.habitacion_id) ?? []; arr.push(r); m.set(r.habitacion_id, arr); });
    return m;
  }, [rack]);
  const bloqueosPorHabitacion = useMemo(() => {
    const m = new Map<string, Bloqueo[]>();
    (rack?.bloqueos ?? []).forEach((b) => { if (!b.habitacion_id) return; const arr = m.get(b.habitacion_id) ?? []; arr.push(b); m.set(b.habitacion_id, arr); });
    return m;
  }, [rack]);

  // ¿La habitación está ocupada (reserva o bloqueo) ese día?
  const ocupada = useMemo(() => {
    const cache = new Map<string, boolean>();
    return (habId: string, dia: string) => {
      const k = `${habId}|${dia}`;
      const v = cache.get(k);
      if (v !== undefined) return v;
      const r = (reservasPorHabitacion.get(habId) ?? []).some((x) => ESTADOS_OCUPAN.has(x.estado) && cubre(x.llegada, x.salida, dia))
        || (bloqueosPorHabitacion.get(habId) ?? []).some((b) => cubre(b.desde, b.hasta, dia));
      cache.set(k, r);
      return r;
    };
  }, [reservasPorHabitacion, bloqueosPorHabitacion]);

  const grupos: Grupo[] = useMemo(() => {
    const habs = [...(rack?.habitaciones ?? [])].sort((a, b) => natural(a.numero, b.numero));
    const m = new Map<string, Grupo>();
    if (prefs.agrupar === 'piso') {
      habs.forEach((h) => {
        const p = h.piso?.trim() || '';
        const key = `piso:${p}`;
        // El piso puede venir como «3» o como «Piso 3».
        const titulo = !p ? 'Sin piso' : /^\d/.test(p) ? `Piso ${p}` : p;
        const corto = !p ? 'S/P' : `P${p.replace(/^piso\s*/i, '')}`;
        if (!m.has(key)) m.set(key, { key, titulo, corto, habitaciones: [] });
        m.get(key)!.habitaciones.push(h);
      });
      return [...m.values()].sort((a, b) => {
        if (a.key === 'piso:') return 1; if (b.key === 'piso:') return -1;
        return natural(a.key, b.key);
      });
    }
    const orden = disp.map((t) => t.tipo_id);
    habs.forEach((h) => {
      const key = `tipo:${h.tipo_id}`;
      if (!m.has(key)) m.set(key, { key, titulo: nombreTipo.get(h.tipo_id) ?? h.tipo, corto: codigoTipo.get(h.tipo_id) ?? h.tipo, habitaciones: [] });
      m.get(key)!.habitaciones.push(h);
    });
    return [...m.values()].sort((a, b) => orden.indexOf(a.key.slice(5)) - orden.indexOf(b.key.slice(5)));
  }, [rack, prefs.agrupar, disp, nombreTipo, codigoTipo]);

  // Banda «Por asignar»: reservas sin habitación (llegadas de hoy primero), en
  // carriles sin solape por tipo; más los bloqueos por tipo.
  const porAsignar = useMemo(() => {
    const sin = (rack?.reservas ?? []).filter((r) => !r.habitacion_id && ESTADOS_OCUPAN.has(r.estado))
      .sort((a, b) => (a.llegada === hoy ? 0 : 1) - (b.llegada === hoy ? 0 : 1) || a.llegada.localeCompare(b.llegada));
    const porTipo = new Map<string, RackReserva[][]>();
    sin.forEach((r) => {
      const lanes = porTipo.get(r.tipo_id) ?? [];
      let lane = lanes.find((l) => l.every((x) => x.salida <= r.llegada || x.llegada >= r.salida));
      if (!lane) { lane = []; lanes.push(lane); }
      lane.push(r);
      porTipo.set(r.tipo_id, lanes);
    });
    const bloqueosTipo = new Map<string, Bloqueo[]>();
    (rack?.bloqueos ?? []).forEach((b) => { if (b.habitacion_id || !b.tipo_id) return; const arr = bloqueosTipo.get(b.tipo_id) ?? []; arr.push(b); bloqueosTipo.set(b.tipo_id, arr); });
    return { total: sin.length, hoy: sin.filter((r) => r.llegada === hoy).length, porTipo, bloqueosTipo };
  }, [rack, hoy]);

  const libresTotal = useMemo(() => dias.map((d) => disp.reduce((s, t) => s + (t.dias.find((x) => x.fecha === d)?.disponibles ?? 0), 0)), [dias, disp]);

  const plegados = useMemo(() => new Set(prefs.plegados), [prefs.plegados]);
  const togglePlegar = (key: string) => {
    const s = new Set(plegados); if (s.has(key)) s.delete(key); else s.add(key);
    setPrefs({ plegados: [...s] });
  };

  const irA = (key: string) => {
    const el = filaRefs.current.get(key);
    const cont = scrollRef.current;
    if (!el || !cont) return;
    // Se descuenta la cabecera fija para que el grupo no quede debajo de ella.
    const altoCabecera = cabeceraRef.current?.offsetHeight ?? 0;
    const top = el.getBoundingClientRect().top - cont.getBoundingClientRect().top + cont.scrollTop - altoCabecera;
    cont.scrollTo({ top, behavior: 'smooth' });
  };

  // Atajo «/» para buscar (fuera de campos de texto).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key === '/' && !(t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable))) {
        e.preventDefault(); buscaRef.current?.focus();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const buscar = () => {
    const q = busca.trim().toLowerCase();
    if (!q || !rack) { setResaltado(null); return; }
    const hab = rack.habitaciones.find((h) => h.numero.toLowerCase() === q)
      ?? rack.habitaciones.find((h) => h.numero.toLowerCase().startsWith(q));
    const reservas = rack.reservas.filter((r) => r.codigo.toLowerCase().includes(q) || (r.huesped ?? '').toLowerCase().includes(q));
    const ids = new Set(reservas.map((r) => r.id));
    const filaId = hab?.id ?? reservas.find((r) => r.habitacion_id)?.habitacion_id ?? null;
    if (!filaId && !reservas.length) { setResaltado({ fila: null, reservas: ids }); return; }
    setResaltado({ fila: filaId, reservas: ids });
    if (filaId) {
      const g = grupos.find((x) => x.habitaciones.some((h) => h.id === filaId));
      if (g && plegados.has(g.key)) togglePlegar(g.key);
      setTimeout(() => irA(`hab:${filaId}`), 50);
    } else {
      setVerPorAsignar(true);
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const sinCoincidencias = resaltado && !resaltado.fila && resaltado.reservas.size === 0;

  const gridTemplate = `${COL_INFO}px repeat(${nDias}, minmax(${colDia}px, 1fr))`;
  const gridWidth = COL_INFO + nDias * colDia;

  const onMoverEnBarras = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.floor(((e.clientX - r.left) / r.width) * nDias);
    if (i !== colHover) setColHover(i >= 0 && i < nDias ? i : null);
  };
  const fondo = <FondoDias dias={dias} hoy={hoy} colHover={colHover} />;

  const ocupHoy = (g: Grupo) => g.habitaciones.filter((h) => ocupada(h.id, hoy)).length;

  // 948: aviso «habitación lista» de housekeeping; el rack la resalta 10 minutos.
  const listas = useAvisosHabitacionLista();
  const numeroHab = useMemo(() => new Map((rack?.habitaciones ?? []).map((h) => [h.id, h.numero])), [rack]);
  const abrirReserva = (id: string, accion?: Accion) => { setPreview(null); setDrawer({ tipo: 'reserva', id, accion }); };
  const colDe = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return Math.floor(((e.clientX - r.left) / r.width) * nDias);
  };
  // Celda libre y no pasada: se puede iniciar una reserva ahí.
  const celdaLibre = (habId: string, i: number) => i >= 0 && i < nDias && dias[i] >= hoy && !ocupada(habId, dias[i]);

  // Selección de noches con el ratón: mousedown en una celda libre, arrastre en
  // la misma fila (se detiene en la primera noche ocupada), mouseup abre la reserva.
  const iniciarSeleccion = (e: React.MouseEvent<HTMLDivElement>, h: Habitacion) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return;
    const i = colDe(e);
    if (!celdaLibre(h.id, i)) return;
    let max = i;
    while (max + 1 < nDias && celdaLibre(h.id, max + 1)) max++;
    setSel({ habId: h.id, tipoId: h.tipo_id, i0: i, i1: i, max });
  };
  const moverSeleccion = (e: React.MouseEvent<HTMLDivElement>, habId: string) => {
    onMoverEnBarras(e);
    if (hoverFila !== habId) setHoverFila(habId);
    if (sel && sel.habId === habId) {
      const i = Math.max(sel.i0, Math.min(sel.max, colDe(e)));
      if (i !== sel.i1) setSel({ ...sel, i1: i });
    }
  };
  useEffect(() => {
    if (!sel) return;
    const up = () => {
      setSel(null);
      setDrawer({ tipo: 'nueva', inicial: { tipoId: sel.tipoId, habitacionId: sel.habId, llegada: dias[sel.i0], salida: sumarDias(dias[sel.i1], 1) } });
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [sel, dias]);

  // ── Arrastrar y soltar ─────────────────────────────────────────────────
  const destinoDe = (a: Arrastre) => {
    const r = a.r;
    let llegada = r.llegada; let salida = r.salida; let hab: string | null = r.habitacion_id;
    if (a.modo === 'mover') {
      if (r.estado !== 'en_casa') { llegada = sumarDias(llegada, a.dd); salida = sumarDias(salida, a.dd); }
      if (a.hab !== undefined) hab = a.hab;
    } else if (a.modo === 'fin') salida = sumarDias(r.salida, a.dd);
    else llegada = sumarDias(r.llegada, a.dd);
    return { llegada, salida, hab };
  };
  // Chequeo local para pintar la sombra (verde/roja); el servidor decide al confirmar.
  const validoLocal = (r: RackReserva, llegada: string, salida: string, hab: string | null) => {
    if (salida <= llegada) return false;
    if (r.estado !== 'en_casa' && llegada < hoy) return false;
    if (r.estado === 'en_casa' && (salida <= hoy || hab === null)) return false;
    if (hab === null) return true;
    const desdeOcc = r.estado === 'en_casa' && hoy > llegada ? hoy : llegada;
    const choca = (reservasPorHabitacion.get(hab) ?? []).some((o) => o.id !== r.id && ESTADOS_ACTIVOS.has(o.estado) && o.llegada < salida && desdeOcc < o.salida);
    const bloq = (bloqueosPorHabitacion.get(hab) ?? []).some((b) => b.desde < salida && desdeOcc < b.hasta);
    return !choca && !bloq;
  };
  const empezarArrastre = (e: React.PointerEvent<HTMLElement>, r: RackReserva, modo: ModoArrastre) => {
    // Solo con ratón: en tablet se usan las acciones de la vista previa.
    if (e.button !== 0 || e.pointerType !== 'mouse' || !MOVIBLES.has(r.estado)) return;
    const fila = e.currentTarget.closest<HTMLElement>('[data-fila]');
    if (!fila) return;
    const a: Arrastre = { r, modo, x0: e.clientX, y0: e.clientY, colW: fila.getBoundingClientRect().width / nDias, activo: false, dd: 0, hab: r.habitacion_id };
    dragRef.current = a; arrastradoRef.current = false; setDrag(a);
  };
  // useLayoutEffect: los oyentes quedan puestos antes de que termine un clic rápido.
  useLayoutEffect(() => {
    if (!drag) return;
    let ultimo = { x: drag.x0, y: drag.y0 };
    const recalcular = () => {
      const a = dragRef.current; if (!a) return;
      const dx = ultimo.x - a.x0; const dy = ultimo.y - a.y0;
      if (!a.activo && Math.hypot(dx, dy) < 6) return;
      let dd = Math.round(dx / a.colW);
      if (a.modo === 'mover' && a.r.estado === 'en_casa') dd = 0; // en casa: solo cambia de habitación
      let hab = a.hab;
      if (a.modo === 'mover') {
        const fila = document.elementFromPoint(ultimo.x, ultimo.y)?.closest<HTMLElement>('[data-fila]');
        if (fila) hab = fila.dataset.hab ? fila.dataset.hab : null;
      }
      if (a.activo && dd === a.dd && hab === a.hab) return;
      const n = { ...a, activo: true, dd, hab };
      dragRef.current = n; arrastradoRef.current = true; setDrag(n);
    };
    const move = (e: PointerEvent) => { ultimo = { x: e.clientX, y: e.clientY }; recalcular(); };
    const up = () => {
      const a = dragRef.current;
      dragRef.current = null; setDrag(null);
      if (!a?.activo) return;
      const { llegada, salida, hab } = destinoDe(a);
      const d: Movimiento['d'] = {};
      if (hab !== a.r.habitacion_id) d.habitacion_id = hab;
      if (llegada !== a.r.llegada) d.llegada = llegada;
      if (salida !== a.r.salida) d.salida = salida;
      if (Object.keys(d).length) setMov({ r: a.r, d });
    };
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') { dragRef.current = null; setDrag(null); } };
    // Desplazamiento automático cerca de los bordes: así se cruza de piso arrastrando.
    const auto = setInterval(() => {
      const cont = scrollRef.current; const a = dragRef.current;
      if (!cont || !a?.activo || a.modo !== 'mover') return;
      const rc = cont.getBoundingClientRect();
      const paso = ultimo.y < rc.top + 50 ? -18 : ultimo.y > rc.bottom - 40 ? 18 : 0;
      if (paso) { cont.scrollBy(0, paso); recalcular(); }
    }, 40);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('keydown', tecla);
    return () => { clearInterval(auto); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('keydown', tecla); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.r.id, drag?.modo]);

  const dragDestino = drag?.activo ? destinoDe(drag) : null;
  const dragValido = drag && dragDestino ? validoLocal(drag.r, dragDestino.llegada, dragDestino.salida, dragDestino.hab) : false;
  const sombra = (hab: string | null) => {
    if (!drag || !dragDestino || dragDestino.hab !== hab) return null;
    const start = Math.max(0, diffDias(desde, dragDestino.llegada));
    const end = Math.min(nDias, diffDias(desde, dragDestino.salida));
    if (end <= start) return null;
    const noches = diffDias(dragDestino.llegada, dragDestino.salida);
    return (
      <div className={`pointer-events-none absolute inset-y-0 z-[3] flex items-center overflow-hidden rounded border-2 border-dashed px-1.5 text-[11px] font-semibold ${
        dragValido ? 'border-emerald-600 bg-emerald-200/70 text-emerald-900 dark:bg-emerald-900/60 dark:text-emerald-100' : 'border-red-600 bg-red-200/70 text-red-900 dark:bg-red-900/60 dark:text-red-100'}`}
        style={{ left: `${(start / nDias) * 100}%`, width: `${((end - start) / nDias) * 100}%` }}>
        <span className="truncate">{dragDestino.llegada.slice(8)}/{dragDestino.llegada.slice(5, 7)} → {dragDestino.salida.slice(8)}/{dragDestino.salida.slice(5, 7)} · {noches} n{dragValido ? '' : ' · no disponible'}</span>
      </div>
    );
  };
  const clicBarra = (r: RackReserva, el: HTMLElement) => {
    if (arrastradoRef.current) { arrastradoRef.current = false; return; }
    setPreview({ r, rect: el.getBoundingClientRect() });
  };

  // La vista previa (panel sobre la grilla) se cierra con Esc.
  useEffect(() => {
    if (!preview) return;
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreview(null); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [preview]);

  return (
    <>
      {/* Detalle o nueva reserva: página que reemplaza al rack (sin ventanas
          encima). El rack queda montado y oculto: al volver conserva la semana,
          el scroll y lo plegado. */}
      {drawer && (
        <div className="p-4">
          {drawer.tipo === 'reserva'
            ? <ReservaDetalle key={drawer.id} reservaId={drawer.id} accionInicial={drawer.accion} onClose={() => setDrawer(null)} />
            : <NuevaReserva inicial={drawer.inicial} volverLabel="Rack" onClose={() => setDrawer(null)}
                onCreated={(id) => setDrawer({ tipo: 'reserva', id })} />}
        </div>
      )}

      <div className={`space-y-2 p-4 ${drawer ? 'hidden' : ''}`}>
        {/* Barra superior */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="mr-auto">
            <h1 className="flex items-center gap-2 text-lg font-semibold">
              <CalendarDays className="h-5 w-5" /> Rack de habitaciones
              {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </h1>
            <p className="text-xs text-muted-foreground">
              {fmtFecha(dias[0])} – {fmtFecha(dias[dias.length - 1])}
              {rack && <> · fecha de hotel <strong>{fmtFecha(rack.fecha_hotel)}</strong></>}
            </p>
          </div>

          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => setDesde((d) => sumarDias(d, -Math.max(7, Math.floor(nDias / 2))))} title="Atrás">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDesde(hoy)}>Hoy</Button>
            <Button size="sm" variant="outline" onClick={() => setDesde((d) => sumarDias(d, Math.max(7, Math.floor(nDias / 2))))} title="Adelante">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <input
              type="date" value={desde} onChange={(e) => e.target.value && setDesde(e.target.value)}
              className="h-8 rounded-md border bg-background px-2 text-xs" title="Ir a la fecha"
            />
          </div>

          <Segmentado
            valor={String(nDias)} onChange={(v) => setPrefs({ dias: Number(v) as Prefs['dias'] })}
            opciones={[['7', '7 d'], ['14', '14 d'], ['30', '30 d']]} titulo="Días visibles"
          />
          <Segmentado
            valor={prefs.agrupar} onChange={(v) => setPrefs({ agrupar: v as Agrupar })}
            opciones={[['piso', 'Por piso'], ['tipo', 'Por tipo']]} titulo="Agrupar"
          />
          <Segmentado
            valor={prefs.densidad} onChange={(v) => setPrefs({ densidad: v as Densidad })}
            opciones={[['compacta', 'Compacta'], ['comoda', 'Cómoda']]} titulo="Densidad"
          />

          <form className="relative" onSubmit={(e) => { e.preventDefault(); buscar(); }}>
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={buscaRef} value={busca}
              onChange={(e) => { setBusca(e.target.value); if (!e.target.value) setResaltado(null); }}
              placeholder="Habitación, huésped o código  ( / )"
              className={`h-8 w-64 rounded-md border bg-background pl-7 pr-2 text-xs ${sinCoincidencias ? 'border-red-400' : ''}`}
            />
            {sinCoincidencias && <span className="absolute -bottom-4 left-0 text-[10px] text-red-600">Sin coincidencias en estas fechas</span>}
          </form>

          <Leyenda />
        </div>

        {/* Chips de salto */}
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}>Todos</Chip>
          {grupos.map((g) => (
            <Chip key={g.key} onClick={() => { if (plegados.has(g.key)) togglePlegar(g.key); setTimeout(() => irA(g.key), 30); }}
              title={`${g.titulo}: ${ocupHoy(g)} de ${g.habitaciones.length} ocupadas hoy`}>
              {g.corto} <span className="text-muted-foreground tabular-nums">{ocupHoy(g)}/{g.habitaciones.length}</span>
            </Chip>
          ))}
          <Chip
            onClick={() => { setVerPorAsignar(true); scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className={porAsignar.total ? 'border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300' : ''}
          >
            <Inbox className="h-3 w-3" /> Por asignar ({porAsignar.total}){porAsignar.hoy ? ` · ${porAsignar.hoy} hoy` : ''}
          </Chip>
          <span className="ml-auto flex gap-1">
            <Chip onClick={() => setPrefs({ plegados: grupos.map((g) => g.key) })}>Plegar todo</Chip>
            <Chip onClick={() => setPrefs({ plegados: [] })}>Expandir todo</Chip>
          </span>
        </div>

        {/* Vista previa y confirmación: paneles integrados sobre la grilla */}
        {mov && <ConfirmarMovimiento mov={mov} onCerrar={() => setMov(null)} />}
        {preview && !mov && (
          <VistaPrevia
            r={preview.r} hoy={hoy} habitacion={preview.r.habitacion_id ? numeroHab.get(preview.r.habitacion_id) ?? null : null}
            onCerrar={() => setPreview(null)} onAbrir={abrirReserva}
            onMover={(d) => { setMov({ r: preview.r, d }); setPreview(null); }}
          />
        )}

        <Card>
          <CardContent className="p-0">
            <div ref={scrollRef} className={`relative overflow-auto ${drag?.activo ? (drag.modo === 'mover' ? 'cursor-grabbing' : 'cursor-ew-resize') : ''}`} style={{ maxHeight: 'calc(100vh - 230px)', minHeight: 320 }}>
              <div style={{ minWidth: gridWidth }} onMouseLeave={() => setColHover(null)}>
                {/* Cabecera fija: días + libres */}
                <div ref={cabeceraRef} className="sticky top-0 z-20 bg-card shadow-sm">
                  <Fila gridTemplate={gridTemplate}>
                    <div className="sticky left-0 z-30 border-b border-r bg-card px-2 py-1.5 text-xs font-semibold">Habitación</div>
                    {dias.map((d, i) => (
                      <div
                        key={d}
                        className={`border-b px-0.5 py-1 text-center text-[11px] leading-tight ${
                          d === hoy ? 'bg-primary/15 font-semibold text-primary' : esFinde(d) ? 'bg-muted/60 text-muted-foreground' : 'text-muted-foreground'
                        } ${colHover === i ? 'ring-1 ring-inset ring-primary/40' : ''}`}
                      >
                        <div className="capitalize">{new Date(`${d}T12:00:00`).toLocaleDateString(LOCALE, { weekday: nDias === 30 ? 'narrow' : 'short' })}</div>
                        <div className="tabular-nums">{d.slice(8, 10)}{nDias === 7 && ` ${new Date(`${d}T12:00:00`).toLocaleDateString(LOCALE, { month: 'short' })}`}</div>
                      </div>
                    ))}
                  </Fila>
                  <Fila gridTemplate={gridTemplate}>
                    <button
                      type="button" onClick={() => setPrefs({ libresDetalle: !prefs.libresDetalle })}
                      className="sticky left-0 z-30 flex items-center gap-1 border-b border-r bg-muted/60 px-2 py-1 text-left text-xs font-medium"
                      title="Ver libres por tipo"
                    >
                      <ChevronDown className={`h-3 w-3 transition-transform ${prefs.libresDetalle ? '' : '-rotate-90'}`} /> Libres
                    </button>
                    {dias.map((d, i) => (
                      <div key={d} className={`border-b px-1 py-1 text-center text-xs tabular-nums ${
                        libresTotal[i] === 0 ? 'bg-red-50 font-semibold text-red-600 dark:bg-red-950/30 dark:text-red-400' : 'bg-muted/30 font-medium'
                      }`}>{libresTotal[i]}</div>
                    ))}
                  </Fila>
                  {prefs.libresDetalle && disp.map((t) => (
                    <Fila key={`disp-${t.tipo_id}`} gridTemplate={gridTemplate}>
                      <div className="sticky left-0 z-30 truncate border-b border-r bg-card py-0.5 pl-6 pr-2 text-[11px] text-muted-foreground" title={t.nombre}>{t.nombre}</div>
                      {dias.map((d) => {
                        const info = t.dias.find((x) => x.fecha === d);
                        const n = info?.disponibles ?? 0;
                        return (
                          <div key={d} title={info?.cerrada ? 'Tarifa cerrada ese día' : undefined}
                            className={`border-b px-1 py-0.5 text-center text-[11px] tabular-nums ${n === 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'} ${info?.cerrada ? 'italic' : ''}`}>
                            {n}
                          </div>
                        );
                      })}
                    </Fila>
                  ))}
                </div>

                {/* Banda «Por asignar» */}
                {(porAsignar.total > 0 || porAsignar.bloqueosTipo.size > 0 || (drag?.activo && drag.r.estado !== 'en_casa' && drag.modo === 'mover')) && (
                  <div className="border-b-2 border-amber-300 dark:border-amber-700">
                    <Fila gridTemplate={gridTemplate}>
                      <button type="button" onClick={() => setVerPorAsignar((v) => !v)}
                        className="sticky left-0 z-10 flex items-center gap-1 border-b border-r bg-amber-50 px-2 py-1 text-left text-xs font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                        <ChevronDown className={`h-3 w-3 transition-transform ${verPorAsignar ? '' : '-rotate-90'}`} />
                        Por asignar ({porAsignar.total})
                      </button>
                      <div data-fila="" data-hab="" className="relative border-b bg-amber-50/50 px-2 py-1 text-[11px] text-amber-800 dark:bg-amber-950/20 dark:text-amber-300" style={{ gridColumn: `2 / span ${nDias}` }}>
                        {drag?.activo && drag.modo === 'mover' && drag.r.estado !== 'en_casa'
                          ? 'Suelte aquí para quitarle la habitación a la reserva'
                          : porAsignar.hoy > 0 ? `${porAsignar.hoy} llegada(s) hoy sin habitación` : 'Reservas sin habitación y bloqueos por tipo'}
                        {sombra(null)}
                      </div>
                    </Fila>
                    {verPorAsignar && (
                      <>
                        {[...porAsignar.porTipo.entries()].flatMap(([tipoId, lanes]) => lanes.map((lane, li) => (
                          <Fila key={`sa-${tipoId}-${li}`} gridTemplate={gridTemplate}>
                            <div className="sticky left-0 z-10 truncate border-b border-r bg-amber-50/70 px-2 text-xs italic text-muted-foreground dark:bg-amber-950/20" style={{ height: alto, lineHeight: `${alto}px` }}>
                              {li === 0 ? nombreTipo.get(tipoId) ?? 'Tipo' : ''}
                            </div>
                            <div data-fila="" data-hab="" className="relative border-b" style={{ gridColumn: `2 / span ${nDias}`, height: alto }} onMouseMove={onMoverEnBarras}>
                              {fondo}
                              {lane.map((r) => (
                                <BarraReserva key={r.id} r={r} desde={desde} dias={nDias} hoy={hoy} resaltar={resaltado?.reservas.has(r.id)} onClick={(el) => clicBarra(r, el)} onAbrir={() => abrirReserva(r.id)}
                                  onArrastrar={(e, modo) => empezarArrastre(e, r, modo)} atenuar={drag?.activo && drag.r.id === r.id} />
                              ))}
                            </div>
                          </Fila>
                        )))}
                        {[...porAsignar.bloqueosTipo.entries()].map(([tipoId, bs]) => (
                          <Fila key={`bt-${tipoId}`} gridTemplate={gridTemplate}>
                            <div className="sticky left-0 z-10 truncate border-b border-r bg-slate-50 px-2 text-xs italic text-muted-foreground dark:bg-slate-900/40" style={{ height: alto, lineHeight: `${alto}px` }}>
                              Bloqueo · {nombreTipo.get(tipoId) ?? 'Tipo'}
                            </div>
                            <div className="relative border-b" style={{ gridColumn: `2 / span ${nDias}`, height: alto }} onMouseMove={onMoverEnBarras}>
                              {fondo}
                              {bs.map((b) => <BarraBloqueo key={b.id} b={b} desde={desde} dias={nDias} />)}
                            </div>
                          </Fila>
                        ))}
                      </>
                    )}
                  </div>
                )}

                {/* Grupos */}
                {grupos.map((g) => {
                  const plegado = plegados.has(g.key);
                  const sucias = g.habitaciones.filter((h) => h.estado_limpieza === 'sucia').length;
                  const fuera = g.habitaciones.filter((h) => h.estado_mant !== 'ok').length;
                  return (
                    <div key={g.key}>
                      <div ref={(el) => { if (el) filaRefs.current.set(g.key, el); else filaRefs.current.delete(g.key); }}>
                        <Fila gridTemplate={gridTemplate}>
                          <button type="button" onClick={() => togglePlegar(g.key)}
                            className="sticky left-0 z-10 flex items-center gap-1 border-b border-r bg-muted px-2 py-1 text-left text-xs font-semibold"
                            title={`${g.titulo} · ${g.habitaciones.length} hab.${sucias ? ` · ${sucias} sucias` : ''}${fuera ? ` · ${fuera} fuera de servicio` : ''}`}>
                            <ChevronDown className={`h-3 w-3 shrink-0 transition-transform ${plegado ? '-rotate-90' : ''}`} />
                            <span className="truncate">{g.titulo}</span>
                            <span className="ml-auto shrink-0 font-normal text-muted-foreground">{g.habitaciones.length}</span>
                            {sucias > 0 && <span className="shrink-0 rounded bg-red-100 px-1 text-[10px] font-normal text-red-700 dark:bg-red-900/40 dark:text-red-300">{sucias}S</span>}
                          </button>
                          {dias.map((d) => {
                            const occ = g.habitaciones.filter((h) => ocupada(h.id, d)).length;
                            const n = g.habitaciones.length;
                            return (
                              <div key={d} title={`${g.titulo} · ${d}: ${occ} ocupadas, ${n - occ} libres`}
                                className={`border-b px-0.5 py-1 text-center text-[11px] tabular-nums ${plegado ? calor(n ? occ / n : 0) : 'bg-muted/50 text-muted-foreground'}`}>
                                {nDias === 30 ? n - occ : `${occ}/${n}`}
                              </div>
                            );
                          })}
                        </Fila>
                      </div>

                      {!plegado && g.habitaciones.map((h) => {
                        const res = resaltado?.fila === h.id;
                        return (
                          <div key={h.id} ref={(el) => { if (el) filaRefs.current.set(`hab:${h.id}`, el); else filaRefs.current.delete(`hab:${h.id}`); }}>
                            <Fila gridTemplate={gridTemplate} className="group">
                              <FichaHabitacion h={h} codigo={codigoTipo.get(h.tipo_id) ?? h.tipo} densidad={prefs.densidad} alto={alto} resaltar={res} lista={listas.has(h.id)}
                                mostrarTipo={prefs.agrupar === 'piso'} piso={prefs.agrupar === 'tipo' ? h.piso : null} />
                              <div data-fila="" data-hab={h.id} className={`relative select-none border-b ${res ? 'bg-yellow-100/60 dark:bg-yellow-900/20' : 'group-hover:bg-primary/5'}`}
                                style={{ gridColumn: `2 / span ${nDias}`, height: alto }}
                                onMouseMove={(e) => moverSeleccion(e, h.id)} onMouseDown={(e) => iniciarSeleccion(e, h)}
                                onMouseLeave={() => setHoverFila((f) => (f === h.id ? null : f))}>
                                {fondo}
                                {sel?.habId === h.id ? (
                                  <div className="pointer-events-none absolute inset-y-0.5 z-[1] flex items-center justify-center rounded border-2 border-dashed border-primary bg-primary/15 text-[11px] font-medium text-primary"
                                    style={{ left: `${(sel.i0 / nDias) * 100}%`, width: `${((sel.i1 - sel.i0 + 1) / nDias) * 100}%` }}>
                                    {sel.i1 - sel.i0 + 1} noche{sel.i1 > sel.i0 ? 's' : ''}
                                  </div>
                                ) : !sel && !drag && hoverFila === h.id && colHover !== null && celdaLibre(h.id, colHover) && (
                                  <div className="pointer-events-none absolute inset-y-0.5 flex cursor-cell items-center justify-center rounded border border-dashed border-primary/60 text-primary/70"
                                    style={{ left: `${(colHover / nDias) * 100}%`, width: `${100 / nDias}%` }} title="Clic para reservar">
                                    <Plus className="h-3.5 w-3.5" />
                                  </div>
                                )}
                                {(bloqueosPorHabitacion.get(h.id) ?? []).map((b) => <BarraBloqueo key={b.id} b={b} desde={desde} dias={nDias} />)}
                                {sombra(h.id)}
                                {(reservasPorHabitacion.get(h.id) ?? []).map((r) => (
                                  <BarraReserva key={r.id} r={r} desde={desde} dias={nDias} hoy={hoy} resaltar={resaltado?.reservas.has(r.id)} onClick={(el) => clicBarra(r, el)} onAbrir={() => abrirReserva(r.id)}
                                  onArrastrar={(e, modo) => empezarArrastre(e, r, modo)} atenuar={drag?.activo && drag.r.id === r.id} />
                                ))}
                              </div>
                            </Fila>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {!grupos.length && (
                  <div className="py-10 text-center text-sm text-muted-foreground">
                    {rack ? 'Sin habitaciones configuradas.' : 'Cargando…'}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Fila({ children, gridTemplate, className = '' }: { children: ReactNode; gridTemplate: string; className?: string }) {
  return <div className={`grid ${className}`} style={{ gridTemplateColumns: gridTemplate }}>{children}</div>;
}

function Segmentado({ valor, onChange, opciones, titulo }: { valor: string; onChange: (v: string) => void; opciones: [string, string][]; titulo: string }) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border text-xs" role="group" aria-label={titulo} title={titulo}>
      {opciones.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={valor === v}
          className={`px-2 py-1.5 ${valor === v ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function Chip({ children, onClick, title, className = '' }: { children: ReactNode; onClick: () => void; title?: string; className?: string }) {
  return (
    <button type="button" onClick={onClick} title={title}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs hover:bg-muted ${className}`}>
      {children}
    </button>
  );
}

function Leyenda() {
  const [abierta, setAbierta] = useState(false);
  // Se abre dentro de la barra (ocupa una fila completa), no flotando encima.
  return (
    <>
      <Button size="sm" variant={abierta ? 'default' : 'ghost'} onClick={() => setAbierta((v) => !v)} title="Leyenda" aria-expanded={abierta}>
        <HelpCircle className="h-4 w-4" />
      </Button>
      {abierta && (
        <div className="basis-full space-y-2 rounded-md border bg-card p-3 text-xs">
          <div className="font-semibold">Reservas</div>
          <div className="grid grid-cols-2 gap-1">
            {Object.entries(ESTADO_RESERVA).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1">
                <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded ${v.bar} ${TEXTO_BARRA[k] ?? 'text-white'}`}>
                  {(() => { const I = ICONO_ESTADO[k] ?? Check; return <I className="h-2.5 w-2.5" />; })()}
                </span>{v.label}
              </span>
            ))}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded bg-slate-300 dark:bg-slate-600"
                style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0 4px, transparent 4px 8px)' }} />
              Bloqueo
            </span>
          </div>
          <div className="font-semibold">Limpieza</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(LIMPIEZA).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-full ${PUNTO_LIMPIEZA[k]}`} />{v.label}</span>
            ))}
            <span className="inline-flex items-center gap-1"><Wrench className="h-3 w-3 text-red-600" />Fuera de servicio</span>
          </div>
          <div className="font-semibold">Grupo plegado</div>
          <p className="text-muted-foreground">Cada día muestra ocupadas/total; el color sube de verde a rojo con la ocupación. Con 30 días se muestran las libres.</p>
          <p className="text-muted-foreground">Clic en una barra: vista previa con acciones · doble clic: abrir la reserva.</p>
          <p className="text-muted-foreground">Clic (o arrastre) en una celda libre: nueva reserva con esa habitación y fechas.</p>
          <p className="text-muted-foreground">Arrastrar una barra: otra habitación (o «Por asignar») u otras fechas; sus bordes estiran la llegada o la salida. Siempre se confirma antes de aplicar.</p>
          <p className="text-muted-foreground">Tecla <kbd className="rounded border px-1">/</kbd> para buscar.</p>
        </div>
      )}
    </>
  );
}

function FichaHabitacion({ h, codigo, densidad, alto, resaltar, mostrarTipo, piso, lista }: {
  h: Habitacion; codigo: string; densidad: Densidad; alto: number; resaltar: boolean; mostrarTipo: boolean; piso: string | null; lista?: boolean;
}) {
  const limp = LIMPIEZA[h.estado_limpieza];
  const mant = h.estado_mant !== 'ok';
  return (
    <div
      className={`sticky left-0 z-10 flex items-center gap-1.5 overflow-hidden border-b border-r px-2 text-xs ${resaltar ? 'bg-yellow-100 ring-2 ring-inset ring-yellow-400 dark:bg-yellow-900/40' : lista ? 'bg-emerald-100 ring-2 ring-inset ring-emerald-500 dark:bg-emerald-900/40' : 'bg-card group-hover:bg-muted'}`}
      style={{ height: alto }}
      title={`${lista ? '¡Recién limpiada! · ' : ''}Hab. ${h.numero} · ${h.tipo}${h.piso ? ` · piso ${h.piso}` : ''} · ${limp?.label ?? h.estado_limpieza}${mant ? ` · ${MANT[h.estado_mant] ?? h.estado_mant}` : ''}`}
    >
      <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${PUNTO_LIMPIEZA[h.estado_limpieza] ?? 'bg-slate-300'}`} aria-label={limp?.label ?? h.estado_limpieza} />
      <span className="font-semibold tabular-nums">{h.numero}</span>
      {mostrarTipo && <span className="truncate text-[11px] text-muted-foreground">{codigo}</span>}
      {piso && <span className="truncate text-[11px] text-muted-foreground">P{piso}</span>}
      {densidad === 'comoda' && limp && <span className={`ml-auto shrink-0 rounded px-1 text-[10px] ${limp.cls}`}>{limp.label}</span>}
      {mant && <Wrench className={`h-3 w-3 shrink-0 text-red-600 ${densidad === 'comoda' ? '' : 'ml-auto'}`} aria-label={MANT[h.estado_mant] ?? h.estado_mant} />}
    </div>
  );
}

// Columnas de fondo de una fila: hoy, fines de semana y la columna bajo el ratón.
function FondoDias({ dias, hoy, colHover }: { dias: string[]; hoy: string; colHover: number | null }) {
  const w = 100 / dias.length;
  return (
    <>
      {dias.map((d, i) => {
        const cls = d === hoy ? 'bg-primary/10 border-x border-primary/30' : colHover === i ? 'bg-primary/5' : esFinde(d) ? 'bg-muted/40' : '';
        return cls ? <div key={d} className={`pointer-events-none absolute inset-y-0 ${cls}`} style={{ left: `${i * w}%`, width: `${w}%` }} /> : null;
      })}
    </>
  );
}

// Icono y color de texto por estado: el estado no depende solo del color de la
// barra, y los fondos claros llevan texto oscuro (contraste).
const ICONO_ESTADO: Record<string, typeof Check> = {
  tentativa: CircleDashed, confirmada: Check, en_casa: Home, salida: LogOut, cancelada: X, no_show: UserX,
};
const TEXTO_BARRA: Record<string, string> = {
  tentativa: 'text-amber-950 border border-dashed border-amber-700', salida: 'text-slate-900', cancelada: 'text-red-950',
};

function BarraReserva({ r, desde, dias, hoy, resaltar, onClick, onAbrir, onArrastrar, atenuar }: {
  r: RackReserva; desde: string; dias: number; hoy: string; resaltar?: boolean;
  onClick: (el: HTMLElement) => void; onAbrir: () => void;
  onArrastrar: (e: React.PointerEvent<HTMLElement>, modo: ModoArrastre) => void; atenuar?: boolean;
}) {
  const movible = MOVIBLES.has(r.estado);
  // La llegada solo se estira antes del check-in y si se ve el inicio real.
  const asaInicio = movible && r.estado !== 'en_casa' && r.llegada >= desde;
  const asaFin = movible && diffDias(desde, r.salida) <= dias;
  const start = Math.max(0, diffDias(desde, r.llegada));
  const end = Math.min(dias, diffDias(desde, r.salida));
  if (end <= start) return null;
  const left = (start / dias) * 100;
  const width = ((end - start) / dias) * 100;
  const est = ESTADO_RESERVA[r.estado];
  const Icono = ICONO_ESTADO[r.estado] ?? Check;
  const llegaHoy = r.llegada === hoy && r.estado !== 'en_casa';
  const saleHoy = r.salida === hoy && r.estado === 'en_casa';
  const pax = `${r.adultos}A${r.ninos ? `+${r.ninos}N` : ''}`;
  const debeDeposito = r.deposito_requerido > r.deposito_pagado + 0.005;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(e.currentTarget); }}
      onDoubleClick={(e) => { e.stopPropagation(); onAbrir(); }}
      onPointerDown={(e) => onArrastrar(e, 'mover')}
      title={`${r.codigo} · ${r.huesped ?? 'Sin nombre'} · ${est.label} — clic: vista previa · doble clic: abrir${movible ? ' · arrastrar: mover' : ''}`}
      aria-label={`Reserva ${r.codigo}, ${r.huesped ?? 'sin nombre'}, ${est.label}, del ${r.llegada} al ${r.salida}, ${pax}${debeDeposito ? ', depósito pendiente' : ''}`}
      className={`absolute inset-y-0.5 z-[2] flex items-center gap-1 overflow-hidden rounded px-1.5 text-left text-[11px] font-medium shadow-sm hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary ${est.bar} ${TEXTO_BARRA[r.estado] ?? 'text-white'} ${resaltar ? 'ring-2 ring-yellow-400 ring-offset-1' : ''} ${atenuar ? 'opacity-40' : ''} ${movible ? 'cursor-grab' : ''}`}
      style={{ left: `${left}%`, width: `${width}%` }}
    >
      {asaInicio && (
        <span aria-hidden title="Arrastrar para cambiar la llegada" onPointerDown={(e) => { e.stopPropagation(); onArrastrar(e, 'inicio'); }}
          className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize hover:bg-black/20" />
      )}
      {asaFin && (
        <span aria-hidden title="Arrastrar para cambiar la salida" onPointerDown={(e) => { e.stopPropagation(); onArrastrar(e, 'fin'); }}
          className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize hover:bg-black/20" />
      )}
      <Icono aria-hidden className="h-3 w-3 shrink-0" />
      {llegaHoy && <span aria-hidden className="shrink-0">→</span>}
      {saleHoy && <span aria-hidden className="shrink-0">←</span>}
      {debeDeposito && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />}
      <span className="truncate">{r.huesped ?? r.codigo} <span className="opacity-80">· {pax} · {r.codigo}</span></span>
    </button>
  );
}

// Vista previa de una reserva con acciones rápidas según su estado.
type Sugerencia = { id: string; numero: string; piso: string | null; estado_limpieza: string; motivo: string };

function VistaPrevia({ r, hoy, habitacion, onCerrar, onAbrir, onMover }: {
  r: RackReserva; hoy: string; habitacion: string | null;
  onCerrar: () => void; onAbrir: (id: string, accion?: Accion) => void; onMover: (d: Movimiento['d']) => void;
}) {
  const [verSug, setVerSug] = useState(false);
  const { data: sugerencias, isFetching: sugiriendo } = useQuery<Sugerencia[]>({
    queryKey: [QK, 'sugerir', r.id], queryFn: () => hotelRpc<Sugerencia[]>('sugerir_habitacion', { _reserva: r.id }),
    enabled: verSug, staleTime: 0,
  });
  const ref = useRef<HTMLDivElement>(null);
  // Al abrir, el foco va a la acción principal (teclado).
  useEffect(() => { ref.current?.querySelector<HTMLButtonElement>('button[data-principal]')?.focus({ preventScroll: true }); }, [r.id]);
  const est = ESTADO_RESERVA[r.estado];
  const noches = diffDias(r.llegada, r.salida);
  const debe = Math.max(0, r.deposito_requerido - r.deposito_pagado);
  const reservable = r.estado === 'tentativa' || r.estado === 'confirmada';
  const acciones: { label: string; accion: Accion; principal?: boolean }[] = [];
  if (reservable) {
    if (r.llegada <= hoy) acciones.push({ label: 'Check-in', accion: 'checkin', principal: true });
    if (debe > 0.005) acciones.push({ label: 'Registrar depósito', accion: 'deposito', principal: r.llegada > hoy });
    acciones.push({ label: r.habitacion_id ? 'Cambiar habitación' : 'Asignar habitación', accion: 'asignar', principal: !r.habitacion_id && r.llegada > hoy && debe <= 0.005 });
  }
  // 948: recepción pide que housekeeping limpie primero la habitación de esta llegada.
  const qc = useQueryClient();
  const pedirLimpieza = useMutation({
    mutationFn: () => hotelRpc('hk_prioridad', { _hab: r.habitacion_id, _activo: true, _motivo: `Llegada ${r.codigo}${r.huesped ? ` · ${r.huesped}` : ''}` }),
    onSuccess: () => { toast.success('Pedido: la habitación sube al primer lugar de la camarera'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (r.estado === 'en_casa') {
    acciones.push({ label: 'Check-out', accion: 'checkout', principal: r.salida <= hoy });
    acciones.push({ label: 'Cambiar habitación', accion: 'mover' });
    acciones.push({ label: 'Modificar salida', accion: 'modificarSalida' });
  }

  return (
    <div ref={ref} role="region" aria-label={`Reserva ${r.codigo}`}
      className="rounded-lg border-2 border-primary/30 bg-card p-3 text-xs shadow-sm">
      <div className="mb-2 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{r.huesped ?? 'Sin nombre'}</div>
          <div className="text-muted-foreground">{r.codigo} · {r.canal}</div>
        </div>
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${est.cls}`}>{est.label}</span>
        <button type="button" onClick={onCerrar} className="rounded p-0.5 hover:bg-muted" aria-label="Cerrar"><X className="h-3.5 w-3.5" /></button>
      </div>
      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
        <dt className="text-muted-foreground">Estadía</dt>
        <dd>{fmtFecha(r.llegada)} → {fmtFecha(r.salida)} · {noches} noche{noches === 1 ? '' : 's'}</dd>
        <dt className="text-muted-foreground">Habitación</dt>
        <dd className="flex items-center gap-1"><BedDouble className="h-3 w-3" />{habitacion ?? <span className="text-amber-700 dark:text-amber-400">sin asignar</span>}</dd>
        <dt className="text-muted-foreground">Huéspedes</dt>
        <dd>{r.adultos} adulto{r.adultos === 1 ? '' : 's'}{r.ninos ? ` · ${r.ninos} niño${r.ninos === 1 ? '' : 's'}` : ''}</dd>
        <dt className="text-muted-foreground">Total</dt>
        <dd className="tabular-nums">{money(r.total)}</dd>
        {r.deposito_requerido > 0 && (<>
          <dt className="text-muted-foreground">Depósito</dt>
          <dd className={`tabular-nums ${debe > 0.005 ? 'text-amber-700 dark:text-amber-400' : ''}`}>
            {money(r.deposito_pagado)} de {money(r.deposito_requerido)}{debe > 0.005 ? ` · falta ${money(debe)}` : ' ✓'}
          </dd>
        </>)}
      </dl>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {acciones.map((a) => (
          <Button key={a.label} size="sm" variant={a.principal ? 'default' : 'outline'} data-principal={a.principal ? '' : undefined}
            onClick={() => onAbrir(r.id, a.accion)}>{a.label}</Button>
        ))}
        {reservable && r.habitacion_id && r.llegada <= hoy && (
          <Button size="sm" variant="outline" isLoading={pedirLimpieza.isPending} onClick={() => pedirLimpieza.mutate()}>
            <Zap className="mr-1 h-3.5 w-3.5" /> Pedir limpieza prioritaria
          </Button>
        )}
        {reservable && (
          <Button size="sm" variant="outline" onClick={() => setVerSug((v) => !v)} aria-expanded={verSug}>
            <Sparkles className="mr-1 h-3.5 w-3.5" /> Sugerir habitación
          </Button>
        )}
        <Button size="sm" variant="ghost" data-principal={acciones.some((a) => a.principal) ? undefined : ''} onClick={() => onAbrir(r.id)}>
          {r.estado === 'en_casa' ? 'Abrir · folio' : 'Abrir reserva'}
        </Button>
      </div>
      {verSug && (
        <div className="mt-2 space-y-1 border-t pt-2">
          {sugiriendo && <div className="text-muted-foreground">Buscando…</div>}
          {!sugiriendo && !sugerencias?.length && <div className="text-muted-foreground">No hay otra habitación libre de este tipo en esas fechas.</div>}
          {sugerencias?.map((s, i) => (
            <button key={s.id} type="button" onClick={() => onMover({ habitacion_id: s.id })}
              className={`flex w-full items-center gap-2 rounded border px-2 py-1 text-left hover:bg-muted ${i === 0 ? 'border-primary' : ''}`}>
              <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${PUNTO_LIMPIEZA[s.estado_limpieza] ?? 'bg-slate-300'}`} />
              <span className="font-semibold tabular-nums">{s.numero}</span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{s.motivo}</span>
              {i === 0 && <span className="shrink-0 text-[10px] text-primary">mejor</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Confirmación de un movimiento del rack ─────────────────────────────────
type Simulacion = {
  ok: boolean; error: string | null; accion: string | null; modo: string | null; diferencia: number | null;
  requiere_motivo: boolean; cambia_tipo: boolean; avisos: string[];
  antes: FotoReserva; despues: FotoReserva | null;
};
type FotoReserva = { habitacion: string | null; tipo: string; llegada: string; salida: string; noches: number; total: number };
const TITULO_ACCION: Record<string, string> = {
  asignar: 'Asignar habitación', desasignar: 'Quitar la habitación', fechas: 'Cambiar fechas', mover_fechas: 'Cambiar habitación y fechas',
  cambio_tipo: 'Cambiar tipo y habitación', mover: 'Cambiar de habitación (huésped en casa)', salida: 'Cambiar la salida',
};

function ConfirmarMovimiento({ mov, onCerrar }: { mov: Movimiento; onCerrar: () => void }) {
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const [cortesia, setCortesia] = useState(false);
  const { data: sim, isFetching } = useQuery<Simulacion>({
    queryKey: [QK, 'rack-simular', mov.r.id, mov.d, cortesia],
    queryFn: () => hotelRpc<Simulacion>('rack_simular', { _reserva: mov.r.id, _d: { ...mov.d, cobrar_diferencia: !cortesia } }),
    staleTime: 0, gcTime: 0, retry: false,
  });
  const aplicar = useMutation({
    mutationFn: () => hotelRpc<{ accion: string }>('rack_aplicar', { _reserva: mov.r.id, _d: { ...mov.d, motivo: motivo.trim(), cobrar_diferencia: !cortesia } }),
    onSuccess: () => {
      toast.success(`${TITULO_ACCION[sim?.accion ?? ''] ?? 'Cambio'} · ${mov.r.codigo} listo`);
      void qc.invalidateQueries({ queryKey: [QK] });
      onCerrar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  useEffect(() => {
    const t = (e: KeyboardEvent) => { if (e.key === 'Escape' && !aplicar.isPending) onCerrar(); };
    window.addEventListener('keydown', t);
    return () => window.removeEventListener('keydown', t);
  }, [onCerrar, aplicar.isPending]);

  const a = sim?.antes; const d = sim?.despues;
  const faltaMotivo = !!sim?.requiere_motivo && motivo.trim().length < 5;
  const fila = (label: string, x: ReactNode, y: ReactNode, cambia: boolean) => (
    <tr className={cambia ? 'font-semibold' : 'text-muted-foreground'}>
      <td className="py-0.5 pr-3">{label}</td><td className="py-0.5 pr-3">{x}</td><td className="py-0.5">{cambia ? <span className="text-primary">{y}</span> : y}</td>
    </tr>
  );
  return (
    <div>
      <div role="region" aria-label="Confirmar cambio" className="max-w-2xl space-y-3 rounded-lg border-2 border-primary/40 bg-card p-4 text-sm shadow-sm">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <div className="text-base font-semibold">{TITULO_ACCION[sim?.accion ?? ''] ?? 'Confirmar cambio'}</div>
            <div className="text-xs text-muted-foreground">{mov.r.codigo} · {mov.r.huesped ?? 'Sin nombre'}</div>
          </div>
          <button type="button" onClick={onCerrar} className="rounded p-1 hover:bg-muted" aria-label="Cerrar"><X className="h-4 w-4" /></button>
        </div>

        {!sim && <div className="flex items-center gap-2 py-4 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Verificando…</div>}

        {sim && !sim.ok && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
            No se puede: {sim.error}
          </div>
        )}

        {sim?.ok && a && d && (
          <>
            <table className="w-full text-xs">
              <thead><tr className="text-left text-muted-foreground"><th className="pb-1 font-normal" /><th className="pb-1 font-normal">Antes</th><th className="pb-1 font-normal">Después</th></tr></thead>
              <tbody>
                {fila('Habitación', a.habitacion ?? 'sin asignar', d.habitacion ?? 'sin asignar', a.habitacion !== d.habitacion)}
                {fila('Tipo', a.tipo, d.tipo, a.tipo !== d.tipo)}
                {fila('Llegada', fmtFecha(a.llegada), fmtFecha(d.llegada), a.llegada !== d.llegada)}
                {fila('Salida', fmtFecha(a.salida), fmtFecha(d.salida), a.salida !== d.salida)}
                {fila('Noches', a.noches, d.noches, a.noches !== d.noches)}
                {fila('Total', money(a.total), money(d.total), Number(a.total) !== Number(d.total))}
              </tbody>
            </table>
            {sim.diferencia !== null && Math.abs(sim.diferencia) >= 0.01 && (
              <div className={`rounded-md px-3 py-2 text-xs ${sim.diferencia > 0 ? 'bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200' : 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'}`}>
                {sim.diferencia > 0 ? `El huésped pagará ${money(sim.diferencia)} más.` : `El total baja ${money(-sim.diferencia)}.`}
              </div>
            )}
            {sim.requiere_motivo && sim.cambia_tipo && (
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={cortesia} onChange={(e) => setCortesia(e.target.checked)} />
                Upgrade de cortesía: no cobrar la diferencia (lo autoriza un supervisor)
              </label>
            )}
            {sim.requiere_motivo && (
              <label className="block text-xs">
                <span className="mb-1 block font-medium">Motivo del cambio (obligatorio)</span>
                <input autoFocus value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="p. ej. ruido, aire acondicionado, pedido del huésped"
                  className="h-8 w-full rounded-md border bg-background px-2 text-xs" />
              </label>
            )}
            {sim.avisos.map((x) => (
              <div key={x} className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">{x}</div>
            ))}
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onCerrar} disabled={aplicar.isPending}>{sim && !sim.ok ? 'Cerrar' : 'Cancelar'}</Button>
          {sim?.ok && (
            <Button size="sm" onClick={() => aplicar.mutate()} disabled={faltaMotivo || isFetching || aplicar.isPending} isLoading={aplicar.isPending}>
              Confirmar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function BarraBloqueo({ b, desde, dias }: { b: Bloqueo; desde: string; dias: number }) {
  const start = Math.max(0, diffDias(desde, b.desde));
  const end = Math.min(dias, diffDias(desde, b.hasta));
  if (end <= start) return null;
  const left = (start / dias) * 100;
  const width = ((end - start) / dias) * 100;
  return (
    <div
      title={b.motivo ?? 'Bloqueo'}
      className="absolute inset-y-0.5 flex items-center overflow-hidden rounded bg-slate-300 px-1.5 text-[11px] text-slate-700 dark:bg-slate-600 dark:text-slate-100"
      style={{
        left: `${left}%`, width: `${width}%`,
        backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.15) 0 6px, transparent 6px 12px)',
      }}
    >
      <span className="truncate">{b.motivo ?? 'Bloqueado'}</span>
    </div>
  );
}
